/**
 * YUMIX — Native-feel Pull-to-Refresh.
 *
 * Detects a downward swipe at the top of the page (touchstart at scrollTop=0
 * → touchmove drags down) and triggers `onRefresh` once the user crosses the
 * threshold. Animates a spinner that respects YUMIX brand colors.
 *
 * Mobile-only by intent — desktop users have F5/refresh button. We bail out
 * on devices without touch events.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';

const TRIGGER_PX = 70;     // distance the user must drag to fire refresh
const MAX_PULL_PX = 100;   // visual cap on the indicator translation
const RESISTANCE = 2.4;    // dampening — a 240 px finger drag feels like 100 px

export default function PullToRefresh({ onRefresh, language = 'fr', children }) {
  const [pull, setPull] = useState(0);     // current pull distance (post-resistance)
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const armed = useRef(false);             // set when finger started at scrollTop=0

  const fr = language === 'fr';

  const onTouchStart = useCallback((e) => {
    if (refreshing) return;
    // Only arm when the page is scrolled to the top
    const scrollTop =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    if (scrollTop > 4) return; // tolerate sub-pixel scroll values
    startY.current = e.touches[0].clientY;
    armed.current = true;
  }, [refreshing]);

  const onTouchMove = useCallback((e) => {
    if (!armed.current || refreshing || startY.current == null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy <= 0) {
      setPull(0);
      return;
    }
    const damped = Math.min(MAX_PULL_PX, dy / RESISTANCE);
    setPull(damped);
  }, [refreshing]);

  const onTouchEnd = useCallback(async () => {
    if (!armed.current) return;
    armed.current = false;
    startY.current = null;
    if (refreshing) return;
    if (pull >= TRIGGER_PX) {
      setRefreshing(true);
      setPull(TRIGGER_PX); // park the spinner at the threshold during fetch
      try {
        const maybe = onRefresh && onRefresh();
        if (maybe && typeof maybe.then === 'function') {
          await maybe;
        }
      } catch {
        // swallow — never crash the UI on a transient refresh error
      }
      setRefreshing(false);
      // Smooth retract
      setPull(0);
    } else {
      // Smooth retract without firing
      setPull(0);
    }
  }, [pull, refreshing, onRefresh]);

  useEffect(() => {
    // Only attach if touch events exist
    if (typeof window === 'undefined') return;
    if (!('ontouchstart' in window)) return;
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [onTouchStart, onTouchMove, onTouchEnd]);

  const opacity = Math.min(1, pull / TRIGGER_PX);
  const ready = pull >= TRIGGER_PX || refreshing;

  return (
    <>
      {/* Indicator — fixed at top-center, transforms with the pull */}
      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
        style={{
          transform: `translate(-50%, ${Math.max(-40, pull - 40)}px)`,
          transition: refreshing ? 'transform 200ms ease' : (pull === 0 ? 'transform 250ms cubic-bezier(.2,.8,.2,1)' : 'none'),
          opacity,
        }}
        data-testid="pull-to-refresh-indicator"
      >
        <div
          className={`mt-3 px-3 py-1.5 rounded-full border flex items-center gap-2 shadow-lg backdrop-blur-md transition-colors ${
            ready
              ? 'bg-primary/90 border-primary text-primary-foreground'
              : 'bg-card/90 border-border text-foreground'
          }`}
        >
          <Loader2
            className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            style={{
              transform: refreshing ? undefined : `rotate(${(pull / TRIGGER_PX) * 270}deg)`,
              transition: refreshing ? undefined : 'transform 80ms linear',
            }}
          />
          <span className="text-xs font-semibold">
            {refreshing
              ? (fr ? 'Mise à jour…' : 'Refreshing…')
              : ready
                ? (fr ? 'Lâcher pour actualiser' : 'Release to refresh')
                : (fr ? 'Tirer pour actualiser' : 'Pull to refresh')}
          </span>
        </div>
      </div>

      {/* Content — gently pushed down so the spinner has room without jumping */}
      <div
        style={{
          transform: `translateY(${refreshing ? Math.min(pull, TRIGGER_PX) : pull}px)`,
          transition: refreshing
            ? 'transform 200ms ease'
            : (pull === 0 ? 'transform 250ms cubic-bezier(.2,.8,.2,1)' : 'none'),
        }}
      >
        {children}
      </div>
    </>
  );
}
